/* <foot-measure> — the eight-step foot-measurement walkthrough.

   Deliberately thin. The drawer itself (open/close/Esc/focus-trap/scroll-lock)
   is the shared <theme-drawer>; this element owns only "which step are we on",
   and expresses that by writing data-step on the scene and toggling .is-on.
   Every visual transition lives in component-foot-measure.css, so the animation
   runs on the compositor and prefers-reduced-motion disables it in one place.

   Layers declare when they appear with data-from="<step index>", so a layer is
   lit whenever step >= its own index — the drawing accumulates rather than
   swapping frames. */
(function () {
  var STEPS = 8;

  class FootMeasure extends HTMLElement {
    connectedCallback() {
      this.scene = this.querySelector('[data-fm-scene]');
      this.layers = Array.prototype.slice.call(this.querySelectorAll('[data-from]'));
      this.caps = Array.prototype.slice.call(this.querySelectorAll('[data-fm-cap]'));
      this.segs = Array.prototype.slice.call(this.querySelectorAll('[data-fm-seg]'));
      this.count = this.querySelector('[data-fm-count]');
      this.readout = this.querySelector('[data-fm-readout]');
      this.back = this.querySelector('[data-fm-back]');
      this.next = this.querySelector('[data-fm-next]');
      this.nextLabel = this.next && this.next.querySelector('span');
      if (!this.scene) return;

      /* Labels come from Liquid so the copy stays in locales/*, never here. */
      this.labelNext = this.getAttribute('data-label-next') || '';
      this.labelRestart = this.getAttribute('data-label-restart') || '';

      this.onBack = this.onBack.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKey = this.onKey.bind(this);
      if (this.back) this.back.addEventListener('click', this.onBack);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.addEventListener('keydown', this.onKey);

      /* Restart from the top each time the drawer opens — a shopper returning
         to it wants step 01, not wherever they abandoned it. */
      this.drawer = this.closest('theme-drawer');
      if (this.drawer) {
        this.observer = new MutationObserver(function (records) {
          for (var i = 0; i < records.length; i++) {
            if (this.drawer.classList.contains('is-open')) this.go(0);
          }
        }.bind(this));
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      this.go(0);
    }

    disconnectedCallback() {
      if (this.back) this.back.removeEventListener('click', this.onBack);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      this.removeEventListener('keydown', this.onKey);
      if (this.observer) this.observer.disconnect();
    }

    onBack() { this.go(this.step - 1); }
    /* Past the last step the primary button restarts, matching its label. */
    onNext() { this.go(this.step >= STEPS - 1 ? 0 : this.step + 1); }

    onKey(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(Math.min(STEPS - 1, this.step + 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.step - 1); }
    }

    go(n) {
      var step = Math.max(0, Math.min(STEPS - 1, n));
      this.step = step;
      this.scene.setAttribute('data-step', step);

      this.layers.forEach(function (el) {
        el.classList.toggle('is-on', step >= Number(el.getAttribute('data-from')));
      });
      this.caps.forEach(function (el, i) {
        var on = i === step;
        el.classList.toggle('is-on', on);
        /* Hidden captions leave the a11y tree so a screen reader reads one step. */
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      this.segs.forEach(function (el, i) { el.classList.toggle('is-on', i <= step); });

      if (this.count) this.count.textContent = String(step + 1).padStart(2, '0') + ' / 0' + STEPS;
      if (this.back) this.back.disabled = step === 0;
      if (this.nextLabel) this.nextLabel.textContent = step >= STEPS - 1 ? this.labelRestart : this.labelNext;

      /* The ruler readout tracks the same two shifts the drawing makes: the mark
         line drops 8 units once both feet are outlined (23,2 → 24,0 cm), then the
         5 mm allowance is added on the sixth step (→ 24,5 cm — EU 38). */
      if (this.readout) {
        var mark = 232 + (step >= 4 ? 8 : 0);
        var cm = (mark + (step >= 6 ? 5 : 0)) / 10;
        this.readout.textContent = cm.toFixed(1).replace('.', ',') + ' CM';
      }
    }
  }

  if (!customElements.get('foot-measure')) customElements.define('foot-measure', FootMeasure);
})();
